import { Injectable, inject } from '@angular/core';
import {BaseTask, ProblemInstance, ProblemInstanceService, Visit, VisitList} from "planner-shared";
import {TimeService} from "@shared";
import {DelayLevel, DelayLevelType} from "../../models/delay-level.model";
import {ShiftDelay} from "./models/employee-delay.model";
import {EmployeeRouteTaskDelay} from "./models/employee-route-task-delay.model";
import {map, take} from "rxjs/operators";
import {BehaviorSubject, Observable} from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class RouteDelayService {
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly timeService = inject(TimeService);


    private routeDelays = new BehaviorSubject<ShiftDelay[]>([]);

    public getRouteDelays(): Observable<ShiftDelay[]> {
        return this.routeDelays;
    }

    public getRouteDelaysForWorkshift(workhourId: string): ShiftDelay {
        return this.routeDelays.value.find(employeeDelay => employeeDelay.workhourId === workhourId);
    }

    public evaluateRouteDelays(route: VisitList) {
        this.problemInstanceService.getProblemInstance()
            .pipe(
                take(1),
                map(instanceResult => {
                    return this.calculateDelays(route, instanceResult.problemInstance);
                })
            )
            .subscribe(routeDelays => {
                this.routeDelays.next(routeDelays);
            });
    }

    private calculateDelays(route: VisitList, problemInstance: ProblemInstance): ShiftDelay[] {
        const allTasks = [
            ...problemInstance.tasks,
            ...problemInstance.emTasksNotPatients,
            ...problemInstance.geTasksNotPatients
        ];

        const routeDelays: ShiftDelay[] = [];
        route.routeKeyNumbers.forEach(keyNumber => {
            const employeeDelay = {
                employeeDelayTotal: DelayLevel.NoDelay,
                employeeRouteTasks: []
            } as ShiftDelay;

            const visitTasks = route.tasks.filter(task => task.workHourId === keyNumber.workHourId);
            visitTasks.forEach(visitTask => {
                const task = allTasks.find(task =>
                    task.taskId === visitTask.routePlannerTaskId ||
                    task.originalTaskId === visitTask.taskId
                );
                const delayMinutes =
                    this.estimateTaskDelay(task, visitTask, problemInstance.officeInfo.minutesSlackOnTasksToUsers);

                if (delayMinutes > 0) {
                    const severity = this.calculateDelaySeverity(delayMinutes);
                    const taskDelay = {
                        taskId: visitTask.routePlannerTaskId,
                        taskDelayMinutes: delayMinutes,
                        taskDelayLevel: severity
                    } as EmployeeRouteTaskDelay;
                    employeeDelay.employeeRouteTasks.push(taskDelay);

                    if (severity > employeeDelay.employeeDelayTotal) {
                        employeeDelay.employeeDelayTotal = severity;
                    }
                }
            });
            employeeDelay.workhourId = keyNumber.workHourId;
            routeDelays.push(employeeDelay);
        });
        return routeDelays;
    }

    private estimateTaskDelay(instanceTask: BaseTask, routeTask: Visit, slackTimeOnTasks: number): number {
        let taskToTimeFormatted = this.timeService.convertTime(instanceTask.toTime, true);
        const routeToTimeFormatted = this.timeService.convertTime(routeTask.estimatedEndTime, true);
        taskToTimeFormatted =
            this.timeService.addMinutesToTime(taskToTimeFormatted, slackTimeOnTasks);

        const taskEndTime = new Date(`${instanceTask.toDate}T${taskToTimeFormatted}`);
        const routeTaskEndTime = new Date(`${routeTask.estimatedEndDate}T${routeToTimeFormatted}`);

        if (routeTaskEndTime > taskEndTime) {
            return this.timeService.timeDifference(taskToTimeFormatted, routeToTimeFormatted);
        }
        return 0;
    }

    private calculateDelaySeverity(delayMinutes: number): DelayLevelType {
        if (delayMinutes <= 30) {
            return DelayLevel.ShortDelay;
        } else if (delayMinutes <= 120) {
            return DelayLevel.MediumDelay;
        } else {
            return DelayLevel.SevereDelay;
        }
    }
}
